import { useState, useRef } from "react";
import firebase from "../../firebase/compat";
import { auth, db, timestamp } from "../../firebase/config";
import { client } from "../../hooks/Client";
import { v4 as uuid } from "uuid";
import buildInterviewUpdate from "../../helpers/storytellingHelpers/buildInterviewUpdate";
import buildPrivacyStreamRequest from "../../helpers/storytellingHelpers/buildPrivacyStreamRequest";

// The privacy steps per source, in order. `hint` text is surfaced as a tooltip
// to reassure the user about what each step protects.
const REDACTION_STEPS = [
  {
    key: "redactPII",
    label: "Persoonsgegevens verwijderen",
    hint: "Telefoonnummers, e-mailadressen, BSN, IBAN en postcodes worden automatisch weggehaald.",
  },
  {
    key: "detectContext",
    label: "Herleidbare gegevens verwijderen",
    hint: "Een AI-model controleert op namen, adressen en andere herleidbare informatie.",
  },
];

const PRIVACY_STEPS_BY_MODE = {
  audio: [
    {
      key: "transcribe",
      label: "Audio beveiligd transcriberen",
      hint: "Transcriptie via OVHcloud Whisper op Europese servers.",
    },
    ...REDACTION_STEPS,
  ],
  document: [
    {
      key: "extractDocument",
      label: "Document beveiligd uitlezen",
      hint: "De tekst wordt op Europese servers uit het document gehaald; de onbewerkte tekst verlaat de server niet.",
    },
    ...REDACTION_STEPS,
  ],
};

// Allow-listed error codes from the pipeline, mapped to a specific message.
const STREAM_ERROR_MESSAGES = {
  EMPTY_DOCUMENT: "Geen tekst gevonden in dit document. Is het misschien een scan of een leeg bestand?",
  UNSUPPORTED_DOCUMENT: "Dit bestandstype wordt niet ondersteund. Upload een PDF- of Word-bestand (.docx).",
  DOCUMENT_TOO_LARGE: "Dit bestand is te groot (maximaal 20 MB).",
};

const initialPrivacySteps = (mode) =>
  PRIVACY_STEPS_BY_MODE[mode].map((step) => ({ ...step, status: "pending" }));

const STREAM_URL =
  process.env.NODE_ENV === "development"
    ? "http://127.0.0.1:5001/deccos-app/europe-west1/privacyPipelineStream"
    : "https://europe-west1-deccos-app.cloudfunctions.net/privacyPipelineStream";

/**
 * Shared processing logic for storytelling interview tabs.
 *
 * Two phases, with a manual review step in between:
 *   1. runPrivacy(url)  — transcribe/extract + redact (no OpenAI). → phase "review"
 *   2. (user edits the redacted transcript and clicks Opslaan)
 *   3. save()           — persist + OpenAI quote/anecdote extraction. → phase "done"
 *
 * phase: "idle" | "processing" | "review" | "saving" | "done"
 *
 * @param {object} interview - The StorytellingInterviews doc being processed
 * @param {object} [options]
 * @param {'audio' | 'document'} [options.mode] - Source type: audio chunks (default)
 *   or one uploaded PDF/.docx document
 */
const useInterviewProcessor = (interview, { mode = "audio" } = {}) => {
  const [privacySteps, setPrivacySteps] = useState(() => initialPrivacySteps(mode));
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState("");
  // Extraction metadata from the pipeline (type/chars/truncated) — lets the
  // review screen surface a truncation notice instead of silently cutting text.
  const [documentMeta, setDocumentMeta] = useState(null);

  // Carried from the privacy phase into the (later) manual save.
  const summaryRef = useRef({});
  const urlsRef = useRef([]);

  // Streams the privacy pipeline (OVH Whisper → PII regex → OVH Qwen context
  // detection), updating each step's status live. The server only ever sends the
  // redacted text + a counts-only summary back — raw transcript/PII stays server-side.
  const transcribeAndRedact = async (urls) => {
    setPrivacySteps(initialPrivacySteps(mode));

    const markStep = (key, status) =>
      setPrivacySteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));

    // De server laat alleen ingelogde leden van de organisatie toe.
    const idToken = await auth.currentUser?.getIdToken();
    const { headers, body } = buildPrivacyStreamRequest({
      mode,
      urls,
      language: interview?.LanguageCode,
      compagnyId: client,
      idToken,
    });

    const response = await fetch(STREAM_URL, { method: "POST", headers, body });

    if (!response.ok || !response.body) {
      throw new Error("Privacy-verwerking kon niet starten.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let result = { text: "", summary: {} };
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by a blank line; keep the trailing partial chunk.
      const blocks = buffer.split("\n\n");
      buffer = blocks.pop();

      for (const block of blocks) {
        const jsonStr = block.replace(/^data: /, "").trim();
        if (!jsonStr) continue;
        let event;
        try {
          event = JSON.parse(jsonStr);
        } catch {
          continue;
        }

        if (event.type === "step") {
          markStep(event.step, event.status === "done" ? "done" : "active");
        } else if (event.type === "done") {
          result = event.result || result;
        } else if (event.type === "error") {
          throw new Error(STREAM_ERROR_MESSAGES[event.message] || "Privacy-verwerking is mislukt.");
        }
      }
    }

    return result;
  };

  const generateIndexCompagnion = async (text) => {
    // v2 callable in europe-west1 — the default functions() client targets
    // us-central1 and would miss it.
    const genkitFunc = firebase.app().functions("europe-west1").httpsCallable("generateStorytellingIndexCompagnion");
    const result = await genkitFunc({
      text,
      interviewId: interview.ID,
      compagnyId: client,
      // "deelnemer" (target group) vs "begeleider" (coach/owner) — drives which
      // analysis framing the cloud function uses. Default to participant.
      type: interview.Type || "deelnemer",
      // Read-only: return the analysis as a PROPOSAL. The interview-detail page
      // reviews everything and persists the accepted subset (saveStorytellingAnalysis).
      persist: false,
    });
    return result.data;
  };

  const saveToFirestore = async (text, urls, piiSummary) => {
    const sessionId = sessionStorage.getItem("sessionId") || uuid();
    await db
      .collection("StorytellingInterviews")
      .doc(interview.docid)
      .update(
        buildInterviewUpdate({ mode, text, urls, summary: piiSummary, sessionId, timestamp })
      );
  };

  /**
   * Phase 1 — OVH transcription + PII redaction only. No OpenAI yet.
   * On success the redacted transcript is shown for manual review (phase "review").
   * @param {string|string[]} input - one audio download URL, or several chunk URLs
   */
  const runPrivacy = async (input) => {
    setError(null);
    setPhase("processing");

    try {
      const urls = Array.isArray(input) ? input : [input];
      urlsRef.current = urls;
      const { text, summary, document } = await transcribeAndRedact(urls);
      summaryRef.current = summary || {};
      setDocumentMeta(document || null);
      setTranscript(text);
      setPhase("review");
    } catch (err) {
      console.error(err);
      setError("Er is iets fout gegaan: " + err.message);
      setPhase("idle");
    }
  };

  /**
   * Phase 2 — runs only after the user reviews the transcript and clicks Opslaan.
   * Persists the (possibly edited) transcript, then does the OpenAI quote/anecdote
   * extraction. The transcript is saved before the OpenAI call, so a quota/API
   * error there never loses the reviewed text.
   *
   * Quotes and anecdotes are NOT written here — they are returned as PROPOSALS.
   * The interview-detail page shows them for review and only writes the ones the
   * user approves. Returns the raw index result ({ BelangrjkeQuotes, Anekdotes }).
   */
  const save = async () => {
    setError(null);
    setPhase("saving");

    try {
      await saveToFirestore(transcript, urlsRef.current, summaryRef.current);
      const indexResult = await generateIndexCompagnion(transcript);
      setPhase("done");
      return indexResult;
    } catch (err) {
      console.error(err);
      setError("Er is iets fout gegaan: " + err.message);
      setPhase("review");
      throw err;
    }
  };

  return { privacySteps, phase, error, transcript, setTranscript, documentMeta, runPrivacy, save };
};

export default useInterviewProcessor;
